import { useSearchParam } from 'react-use';
import {
  RegionWrapper,
  HubWrapper,
  CSSchedulerTable,
  SchedulerRegionTotalsTable,
  CSSchedulerControlRow,
  CSHrSystemConflictCard,
} from '@lp/features/Scheduler/SchedulerClinicalSpecialists/components';
import { useMonthScheduleStates, useResourceReservationsToDayMaps } from '@lp/features/Scheduler/hooks';
import {
  useCSScheduleControls,
  useCSSchedulerData,
  useCSSchedulerHubExpand,
  useCSSchedulerMutationActions,
  useCSSchedulerServices,
  useCSSchedulerShiftHighlight,
  useCSScheduleValidation,
  useCSShiftChange,
  useCSUnblockShift,
  useCSWorkdaySync,
  useCSFilteredHubVisibility,
  useCSAutoExpandOnFilterChange,
} from '@lp/features/Scheduler/SchedulerClinicalSpecialists/hooks';
import {
  ConflictsDrawerModal,
  HrSystemConflictsDrawerModal,
  PublishBlockerTooltip,
  SchedulerControlHeader,
  SchedulerLoadingOverlay,
} from '@lp/features/Scheduler/components';
import { getIsValidatable } from '@lp/features/Scheduler/utils';
import {
  csConflictsDrawerData,
  csDeductionRulesConflictsDrawerData,
} from '@lp/features/Scheduler/SchedulerClinicalSpecialists/constants';
import { isCSDeductionRulesMode } from '@lp/features/Scheduler/SchedulerClinicalSpecialists/period.utils';
import { useAuthContext } from '@lp/core/auth';
import { SchedulerPermissions } from '@/permissions';

type SchedulerClinicalSpecialistsProps = {
  readonly?: boolean;
};

export const SchedulerClinicalSpecialists = ({ readonly }: SchedulerClinicalSpecialistsProps) => {
  const navigationHubId = useSearchParam('hubId');
  const {
    startDay,
    selectedDate,
    selectedDateRange,
    daysInterval,
    onMonthPrev,
    onMonthNext,
    onDateChange,
    isReservationsView,
    setIsReservationsView,
    periodLabel,
    periodDateRange,
  } = useCSScheduleControls();
  const {
    shiftsData,
    isShiftsLoading,
    monthSchedule,
    isPlanningMode,
    togglePlanningMode,
    planningData,
    reservationsData,
  } = useCSSchedulerData({ startDay, dateRange: selectedDateRange, isReservationsView });
  const { regionList, regionsDictionary, hubsDictionary, hubToResourceMap, resourceShiftTotalsMap } =
    useCSSchedulerServices(selectedDate, shiftsData);
  const hubVisibility = useCSFilteredHubVisibility(regionList, hubToResourceMap);
  useCSAutoExpandOnFilterChange(hubVisibility);
  const { visibleHubIds, visibleRegionIds, hasResourceFilters } = hubVisibility;
  const reservationsToDayMaps = useResourceReservationsToDayMaps(reservationsData);
  const onShiftChange = useCSShiftChange(selectedDateRange, monthSchedule, isPlanningMode);
  const { checkPermission } = useAuthContext();
  const canUnblockShifts = checkPermission(SchedulerPermissions.CSSchedulesFixationTypeWrite);
  const onUnblockShift = useCSUnblockShift(selectedDateRange, monthSchedule, isPlanningMode);
  const workdayData = useCSWorkdaySync(startDay, selectedDateRange, monthSchedule, isPlanningMode, readonly);
  const actions = useCSSchedulerMutationActions(startDay, planningData);
  const isDeductionRulesMode = isCSDeductionRulesMode(startDay);
  const validationData = useCSScheduleValidation(
    planningData,
    shiftsData,
    hubsDictionary,
    regionsDictionary,
    isPlanningMode,
    !isDeductionRulesMode,
  );
  const { isEditable, isGenerating, isResettable } = useMonthScheduleStates(monthSchedule, isPlanningMode, readonly);
  const { expandHub } = useCSSchedulerHubExpand(regionList, hubsDictionary, navigationHubId);
  const { highlightByViolation, highlightResourceEmptyShift } = useCSSchedulerShiftHighlight({
    selectedDate,
    daysInterval,
    hubToResourceMap,
    shiftsData,
    expandHub,
  });
  const showBackdrop = isGenerating || isShiftsLoading;
  const showValidation = getIsValidatable(validationData?.fillingState);
  const initialRegionId = navigationHubId ? hubsDictionary[navigationHubId]?.regionId : null;

  if (!regionList || !shiftsData) return null;

  return (
    <div className="flex flex-col min-h-full">
      <div className="p-6 pb-2 sticky top-0 z-12 bg-base-70">
        <SchedulerControlHeader
          readonly={readonly}
          settingsPath="/schedule/cs/settings"
          actions={actions}
          monthSchedule={monthSchedule}
          planningData={planningData}
          isPlanningMode={isPlanningMode}
          togglePlanningMode={togglePlanningMode}
          violationsCount={validationData?.count}
          validationIsFetching={validationData?.isFetching}
          publishReadinessData={validationData?.publishReadiness}
          fillingState={validationData?.fillingState}
          conflictsButton={
            <ConflictsDrawerModal
              tabsData={isDeductionRulesMode ? csDeductionRulesConflictsDrawerData : csConflictsDrawerData}
              validationData={validationData}
              onItemClick={highlightByViolation}
            />
          }
          hrConflictsButton={
            <HrSystemConflictsDrawerModal hrSystemData={workdayData} ConflictCard={CSHrSystemConflictCard} />
          }
          publishBlockerTooltip={
            <PublishBlockerTooltip
              shiftsData={shiftsData}
              publishReadinessData={validationData?.publishReadiness}
              onResourceClick={highlightResourceEmptyShift}
            />
          }
          startDay={startDay}
          selectedDate={selectedDate}
          periodLabel={periodLabel}
          periodDateRange={periodDateRange}
          onMonthPrev={onMonthPrev}
          onMonthNext={onMonthNext}
        >
          <CSSchedulerControlRow
            readonly={readonly}
            showPhoneSupport={!isDeductionRulesMode}
            startDay={startDay}
            selectedDate={selectedDate}
            daysInterval={daysInterval}
            isPlanningMode={isPlanningMode}
            showValidation={showValidation}
            shiftsData={shiftsData}
            hubsDictionary={hubsDictionary}
            workdayData={workdayData}
            supportViolations={validationData?.supportMessagesList}
            onDateChange={onDateChange}
            isReservationsView={isReservationsView}
            setIsReservationsView={setIsReservationsView}
          />
        </SchedulerControlHeader>
      </div>
      <div className="flex flex-col gap-6 relative p-6 pt-4 h-full">
        {showBackdrop && (
          <SchedulerLoadingOverlay
            isGenerating={isGenerating}
            isMutating={actions.isMutating}
            generationInfo={planningData?.autoSchedulerInfo}
            onGenerationCancel={actions.onGenerationCancel}
          />
        )}

        {hasResourceFilters && visibleRegionIds.size === 0 && (
          <div className="flex items-center justify-center h-full">
            <p className="text-l-m text-base-35">No results for applied filters</p>
          </div>
        )}

        {regionList.map((region, regionIndex) => {
          if (hasResourceFilters && !visibleRegionIds.has(region.id)) return null;
          const isRegionInitiallyExpanded =
            hasResourceFilters || (initialRegionId ? region.id === initialRegionId : regionIndex === 0);

          return (
            <RegionWrapper
              key={region.id}
              region={region}
              withTotals={!readonly}
              initiallyExpanded={isRegionInitiallyExpanded}
              violations={validationData?.byRegionMap[region.id]}
            >
              {(showRegionTotals) => (
                <>
                  {region.hubs.map((hub, hubIndex) => {
                    if (hasResourceFilters && !visibleHubIds.has(hub.id)) return null;

                    const userList = hubToResourceMap?.get(hub.id) ?? [];
                    const hubViolations = validationData?.byHubMap[hub.id];
                    const isHubInitiallyExpanded =
                      hasResourceFilters ||
                      (!!initialRegionId && initialRegionId === region.id
                        ? hub.id === navigationHubId
                        : hubIndex === 0);

                    return (
                      <HubWrapper
                        key={hub.id}
                        hub={hub}
                        readonly={readonly}
                        userList={userList}
                        shiftsData={shiftsData}
                        hubsDictionary={hubsDictionary}
                        violations={hubViolations}
                        initiallyExpanded={isHubInitiallyExpanded}
                        initiallyNavigated={hub.id === navigationHubId}
                      >
                        {(showHubTotals) => (
                          <CSSchedulerTable
                            hub={hub}
                            isEditable={isEditable}
                            isReservationsView={isReservationsView}
                            canResetShifts={isResettable}
                            canUnblockShifts={canUnblockShifts}
                            withHubTotals={showHubTotals}
                            selectedDate={selectedDate}
                            userList={userList}
                            hubsDictionary={hubsDictionary}
                            daysInterval={daysInterval}
                            shiftsData={shiftsData}
                            shiftTotalsMap={resourceShiftTotalsMap}
                            reservationsMap={reservationsToDayMaps}
                            hubViolations={hubViolations}
                            resourceViolations={validationData?.byResourceMap}
                            onShiftChange={onShiftChange}
                            onUnblockShift={canUnblockShifts ? onUnblockShift : undefined}
                          />
                        )}
                      </HubWrapper>
                    );
                  })}
                  {!readonly && showRegionTotals && (
                    <SchedulerRegionTotalsTable
                      region={region}
                      daysInterval={daysInterval}
                      selectedDate={selectedDate}
                      shiftsData={shiftsData}
                      hubToResourceMap={hubToResourceMap}
                    />
                  )}
                </>
              )}
            </RegionWrapper>
          );
        })}
      </div>
    </div>
  );
};
